import { useState } from "react";
import { IonButton, IonIcon, IonInput, IonSelect, IonSelectOption, IonSpinner } from "@ionic/react";
import { mailOutline, sendOutline, closeOutline, linkOutline, personAddOutline, trashOutline } from "ionicons/icons";
import { FLOAT } from "@/components/SettingsRows";
import { isUserLoggedIn, userHeaders } from "@/utils/userAuth";
import { confirmAlert } from "@/utils/confirmAlert";
import { toast } from "@/utils/toast";
import { toastText } from "@/utils/toastTemplates";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const validEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());

// "Send for signature / approval" under a counterparty's signature field
// (Pro/Enterprise). Expands into a panel dressed like the pay stub modals'
// period cards, flush with the card edge: a sub-bar title, the fields two to
// a row. Send stays off until every signer has an email and a name, and it
// confirms (the download it spends, and the refund on a decline or cancel)
// before sending. Sending emails them a link to the public signing page,
// where the final signed PDF is generated and archived back to this account.
//
// props: hasAccess, documentType, fieldName, fieldLabel, taxYear?,
//        getFormData() -> the derived form snapshot (or null to abort),
//        bleed: px from this component's container to the card edge, so the
//        panel runs edge to edge (16 when it sits straight in a card)
export default function SendForSignature({ hasAccess, documentType, fieldName, fieldLabel, taxYear, getFormData, otherFields = [], bleed = 16 }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [sentLink, setSentLink] = useState("");
  const [second, setSecond] = useState(false);
  const [secondField, setSecondField] = useState(otherFields[0]?.name || "");
  const [secondEmail, setSecondEmail] = useState("");
  const [secondName, setSecondName] = useState("");

  if (!hasAccess || !isUserLoggedIn()) return null;

  // Send unlocks once every signer has a real email and a name; the note stays optional.
  const firstReady = validEmail(email) && name.trim().length > 0;
  const secondReady = !second || (validEmail(secondEmail) && secondName.trim().length > 0);
  const ready = firstReady && secondReady;
  const downloadNotice = toastText("sign_download_notice", {}, "Sending a document for signature uses 1 download.");

  const send = async () => {
    if (!ready || busy) return;
    const formData = getFormData();
    if (!formData) return;
    const recipients = second ? `${email.trim()} and ${secondEmail.trim()}` : email.trim();
    const confirmed = await confirmAlert({
      header: "Send for approval?",
      message: `This emails ${recipients} a link to review, sign or decline. ${downloadNotice} The download is returned to you if you cancel the request or the signer declines the document.`,
      confirmText: "Send",
    });
    if (!confirmed) return;
    setBusy(true);
    try {
      const sf = otherFields.find((f) => f.name === secondField);
      const res = await fetch(`${BACKEND_URL}/api/user/signature-requests`, {
        method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          documentType, fieldName, fieldLabel, taxYear,
          recipientEmail: email.trim(), recipientName: name.trim(), note: note.trim(),
          formData,
          ...(second && sf ? { nextSigner: { fieldName: sf.name, fieldLabel: sf.label, recipientEmail: secondEmail.trim(), recipientName: secondName.trim() } } : {}),
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not send");
      setSentTo(email.trim());
      setSentLink(d.link || "");
      setOpen(false);
      toast.success(`Sent to ${email.trim()} for signature`);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="send-for-signature" style={{ margin: "6px 0 2px" }}>
      {sentTo && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 6px", flexWrap: "wrap" }}>
          <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--ion-color-success)" }}>
            Sent to {sentTo}. You're notified when they sign.
          </p>
          {sentLink && (
            <IonButton size="small" fill="clear" className="copy-sign-link" onClick={async () => {
              try { await navigator.clipboard.writeText(sentLink); toast.success("Link copied"); }
              catch { toast.error("Could not copy the link"); }
            }}>
              <IonIcon slot="start" icon={linkOutline} /> Copy link
            </IonButton>
          )}
        </div>
      )}
      {!open ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <IonButton size="small" color="light" onClick={() => setOpen(true)}>
            <IonIcon slot="start" icon={mailOutline} />
            SEND FOR APPROVAL
          </IonButton>
        </div>
      ) : (
        <div className="period-card sign-request-panel" style={{ "--period-bleed": `${bleed}px` }}>
          <div className="admin-subbar sheet-card-head period-head with-action">
            <span>Send for approval{fieldLabel ? `: ${fieldLabel}` : ""}</span>
            <IonButton fill="clear" color="medium" size="small" className="period-head-remove" disabled={busy} onClick={() => setOpen(false)} aria-label="Close">
              <IonIcon slot="icon-only" icon={closeOutline} />
            </IonButton>
          </div>
          <div className="period-form">
            <IonInput {...FLOAT} label="Their email *" type="email" inputmode="email"
              value={email} onIonInput={(e) => setEmail(e.detail.value ?? "")} />
            <IonInput {...FLOAT} label="Their name *"
              value={name} onIonInput={(e) => setName(e.detail.value ?? "")} />
            <IonInput {...FLOAT} className="admin-field period-wide" label="Note (optional)"
              value={note} onIonInput={(e) => setNote(e.detail.value ?? "")} />
            {otherFields.length > 0 && !second && (
              <div className="period-wide">
                <IonButton size="small" fill="clear" onClick={() => setSecond(true)}>
                  <IonIcon slot="start" icon={personAddOutline} /> Add a second signer
                </IonButton>
              </div>
            )}
          </div>
          {second && (
            <>
              <div className="admin-subbar sheet-card-head period-head with-action">
                <span>Second signer (signs after the first)</span>
                <IonButton fill="clear" color="danger" size="small" className="period-head-remove" disabled={busy} onClick={() => setSecond(false)} aria-label="Remove second signer">
                  <IonIcon slot="icon-only" icon={trashOutline} />
                </IonButton>
              </div>
              <div className="period-form">
                {otherFields.length > 1 && (
                  <IonSelect {...FLOAT} className="admin-field period-wide" interface="popover" label="Their signature field"
                    value={secondField} onIonChange={(e) => setSecondField(e.detail.value)}>
                    {otherFields.map((f) => <IonSelectOption key={f.name} value={f.name}>{f.label}</IonSelectOption>)}
                  </IonSelect>
                )}
                <IonInput {...FLOAT} label="Their email *" type="email" inputmode="email"
                  value={secondEmail} onIonInput={(e) => setSecondEmail(e.detail.value ?? "")} />
                <IonInput {...FLOAT} label="Their name *"
                  value={secondName} onIonInput={(e) => setSecondName(e.detail.value ?? "")} />
              </div>
            </>
          )}
          <div className="sign-request-foot">
            <div className="sign-request-actions">
              <IonButton size="small" color="success" disabled={busy || !ready} onClick={send} style={{ "--color": "#ffffff" }}>
                {busy ? <IonSpinner name="crescent" style={{ width: 16, height: 16 }} /> : <><IonIcon slot="start" icon={sendOutline} /> Send</>}
              </IonButton>
              <IonButton size="small" fill="clear" color="medium" disabled={busy} onClick={() => setOpen(false)}>
                <IonIcon slot="start" icon={closeOutline} /> Cancel
              </IonButton>
            </div>
            <p className="sign-request-note">
              <strong>{downloadNotice}</strong>
              {" "}They get an email with a link to review, sign or decline. The signed copy archives to My Documents and you're notified either way. A declined or canceled request refunds the download.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
